import { useMemo, memo } from 'react';
import ReactFlow, { ReactFlowProps, Node, XYPosition, Edge, ReactFlowProvider } from 'reactflow';
import { ConnectorButtonProps } from './components/ConnectorButton';
import { useAutoLayout } from './hooks/useAutoLayout';
import { isDeepEqual } from 'sections/utils/objectDeepEqual';

export interface EdgeData {
  connectorButtonProps: Omit<ConnectorButtonProps, 'labelX' | 'labelY'>;
  allowedNodes?: string[];
}

export interface NodeData {
  children?: string[];
  parentId?: string;
  restrictToType?: string;
  order?: number;
}

export type CanvasNode = Omit<Node<NodeData>, 'position'> & { position?: XYPosition };
export type CanvasEdge = Edge<EdgeData>;

type Elements = {
  nodeMap: Map<string, Node>;
  edgeMap: Map<string, Edge>;
};

export interface CanvasProps extends Omit<ReactFlowProps, 'nodes' | 'onNodesChanges' | 'onEdgedChange'> {
  /**
   * Nodes that will be displayed on the canvas.
   *
   * Note: In order to update node data, a new node array must be passed.
   */
  nodes: CanvasNode[];
}

function compareNodes(xs: Map<string, Node>, ys: Map<string, Node>) {
  // the number of nodes changed, so we already know that the nodes are not equal
  if (xs.size !== ys.size) return false;

  for (const [id, x] of xs.entries()) {
    const y = ys.get(id);

    // the node doesn't exist in the next state so it just got added
    if (!y) return false;
    if (x.width !== y.width || x.height !== y.height) return false;
    if (x.position.x !== y.position.x || x.position.y !== y.position.y) return false;
    if (!isDeepEqual(x.data, y.data)) return false;
  }

  return true;
}

function compareEdges(xs: Map<string, Edge>, ys: Map<string, Edge>) {
  // the number of edges changed, so we already know that the edges are not equal
  if (xs.size !== ys.size) return false;

  for (const [id, x] of xs.entries()) {
    const y = ys.get(id);

    // the edge doesn't exist in the next state so it just got added
    if (!y) return false;
    if (x.source !== y.source || x.target !== y.target) return false;
    if (x?.sourceHandle !== y?.sourceHandle) return false;
    if (x?.targetHandle !== y?.targetHandle) return false;
    if (!isDeepEqual(x.data, y.data)) return false;
  }

  return true;
}

function compareElements(xs: Elements, ys: Elements) {
  return compareNodes(xs.nodeMap, ys.nodeMap) && compareEdges(xs.edgeMap, ys.edgeMap);
}

/**
 * Canvas should only be updated if a node's meta data has been updated.
 *
 * @param oldProps
 * @param newProps
 * @returns
 */
const comparitor = (oldProps: CanvasProps, newProps: CanvasProps) => {
  const oldEl: Elements = {
    nodeMap: oldProps.nodes.reduce((acc, node) => acc.set(node.id, node), new Map()),
    edgeMap: oldProps.edges.reduce((acc, edge) => acc.set(edge.id, edge), new Map()),
  };

  const newEl: Elements = {
    nodeMap: newProps.nodes.reduce((acc, node) => acc.set(node.id, node), new Map()),
    edgeMap: newProps.edges.reduce((acc, edge) => acc.set(edge.id, edge), new Map()),
  };

  return compareElements(oldEl, newEl);
};

const InternalCanvas = memo(function Canvas({ nodes: initialNodes, edges: initialEdges, ...props }: CanvasProps) {
  const mappedNodes = useMemo(() => {
    return initialNodes.map(s => ({ ...s, position: s.position ?? { x: 0, y: 0 } })) as Node[];
  }, [initialNodes]);

  useAutoLayout();

  return (
    <ReactFlow
      {...props}
      fitView
      nodes={mappedNodes}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      edges={initialEdges}
      id="react-flow"
      aria-label="Canvas"
    />
  );
}, comparitor);

export const Canvas = (props: CanvasProps) => {
  return (
    <ReactFlowProvider>
      <InternalCanvas {...props} />
    </ReactFlowProvider>
  );
};
